import type { DrawTick, DrawTickId, RaceStage, WinRule } from '@signalvote/shared';
import { assertNever, drawTicks, placesOf, RaceStageKind, settleDials, voteGoal } from '@signalvote/shared';
import type { StageWork } from './stageGoal';

export type ReadyWork = StageWork & { kind: 'Ready' };

export const fmt = (n: number) => n.toLocaleString('en-US');

export const pct = (share: number, digits = 0) => `${(share * 100).toFixed(digits)}%`;

const DAY = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

/** "Nov 7, 2028" for a date-only ISO string, read as the calendar date it names. */
export function dayWords(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return DAY.format(Date.UTC(y, m - 1, d));
}

/** Whole days from `today` to `isoDate`, both date-only strings in campaign time. */
export function daysUntil(isoDate: string, today: string): number {
  const day = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d) / 86_400_000;
  };
  return day(isoDate) - day(today);
}

export function countdownWords(days: number): string {
  if (days < 0) return 'already held';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${fmt(days)} days`;
}

export function stageName(kind: RaceStageKind): string {
  switch (kind) {
    case RaceStageKind.RepublicanPrimary:
      return 'Republican primary';
    case RaceStageKind.DemocraticPrimary:
      return 'Democratic primary';
    case RaceStageKind.OpenPrimary:
      return 'open primary';
    case RaceStageKind.NonpartisanPrimary:
      return 'primary';
    case RaceStageKind.General:
      return 'general';
    default:
      return assertNever(kind);
  }
}

/** The stage's name to start a line with: "General", "Republican primary". */
export const stageTitle = (kind: RaceStageKind) => stageName(kind).replace(/^./, (c) => c.toUpperCase());

export const isPartyPrimary = (kind: RaceStageKind) => kind === RaceStageKind.RepublicanPrimary || kind === RaceStageKind.DemocraticPrimary;

export const peopleWord = (n: number) => (n <= 1 ? 'just me' : `${n} people`);

/** What reaching the aim does: "win", "win a seat", "make the top 2". */
export function aimBuys(rule: WinRule): string {
  switch (rule.kind) {
    case 'MostVotes':
    case 'MajorityOrRunoff':
    case 'ShareThreshold':
      return 'win';
    case 'VoteForSeveral':
      return 'win a seat';
    case 'TopAdvance':
      return `make the top ${rule.count}`;
    default:
      return assertNever(rule);
  }
}

/** What the aim buys, for the end of the sentence; nothing when there is one winner. */
export const winTail = (rule: WinRule) => (placesOf(rule) === 1 ? '' : ` to ${aimBuys(rule)}`);

/** What the shared math's share means with this many running. */
export function winLabel(rule: WinRule, running: number): string {
  if (rule.kind === 'VoteForSeveral') return `to win one of ${rule.seats ?? 'the'} seats with ${running} running`;
  return `to ${aimBuys(rule)} with ${running} running`;
}

export function runoffBar(rule: WinRule): string {
  return rule.kind === 'ShareThreshold' ? pct(rule.share) : 'half';
}

/** Until the candidate sets the field, say we assumed it and where the count came from. */
export function assumedField(stage: RaceStage, work: ReadyWork): string | null {
  const n = work.dials.candidates;
  if (n !== work.defaults.candidates || n <= 1) return null;
  const last = stage.lastResult;
  const onePlace = placesOf(work.contest.rule) === 1;
  const change = ' Change this once you know who filed.';
  if (last?.candidates === n) return `We assumed ${n} running, like last time.${change}`;
  if (onePlace && last?.firstLoserVotes === null) return 'Last time nobody ran against the winner. We still plan for one opponent.';
  return `We assumed ${onePlace ? 'one opponent' : 'one more candidate than seats'}.${change}`;
}

export function lastResultWords(stage: RaceStage): string | null {
  const last = stage.lastResult;
  if (!last) return null;
  const year = last.date.slice(0, 4);
  if (last.firstLoserVotes === null) return `Last time (${year}): unopposed, with ${fmt(last.totalVotes)} votes.`;
  return `Last time (${year}): ${last.candidates} ran; the winner had ${fmt(last.lastWinnerVotes)} of ${fmt(last.totalVotes)} votes.`;
}

/** The candidate typed their own ballots, so the year is that one number. */
export const ownYear = (work: ReadyWork) => !!work.contest.manual && !work.base.manual;

export function yearWord(work: ReadyWork): string {
  const { turnout, pastTurnout } = work.contest.numbers;
  if (ownYear(work)) return `your ${fmt(turnout.usual)}-ballot`;
  const year = work.dials.year;
  if (year === turnout.busy) return 'busy';
  if (year === turnout.usual) return 'usual';
  if (year === turnout.quiet) return 'quiet';
  const past = pastTurnout.find((p) => p.ballots === year);
  return past ? `${past.year}-like` : `${fmt(year)}-ballot`;
}

/** What's above the race on the ballot, as the popover lists it; the sentence adds {@link ABOVE}. */
export const DRAW_PHRASE: Record<DrawTickId, string> = {
  None: 'nothing big',
  Usual: 'the usual races',
  Race: 'a close race',
  Measure: 'a ballot measure',
  Huge: 'a huge draw',
};

export const ABOVE = ' above mine on the ballot';

export const DRAW_HELP: Record<DrawTickId, string> = {
  None: 'Nothing on top pulls voters out.',
  Usual: 'What this kind of year usually has.',
  Race: 'A close governor’s or Senate race.',
  Measure: 'An amendment or referendum people care about.',
  Huge: 'Something that brings out everyone.',
};

/** Whether the draw dial sits on this mark. */
export const onDrawTick = (tick: DrawTick, draw: number) => Math.abs(tick.points - draw) <= 0.08;

export function drawWord(work: ReadyWork): string {
  const tick = drawTicks(work.contest.cycle).find((t) => onDrawTick(t, work.dials.draw));
  if (tick) return DRAW_PHRASE[tick.id] + ABOVE;
  return `${work.dials.draw > 0 ? '+' : '−'}${Math.abs(work.dials.draw).toFixed(1)} points of turnout from races${ABOVE}`;
}

export const HEATS = [
  { heat: 0.15, label: 'little interest in my race', help: 'Nobody knows it’s on. More voters skip it.' },
  { heat: 0.5, label: 'normal interest in my race', help: 'About as much as races like it. Our number.' },
  { heat: 0.85, label: 'a lot of interest in my race', help: 'Mailers every day. Almost nobody skips it.' },
] as const;

export const heatWord = (work: ReadyWork) => (work.dials.heat < 0.34 ? HEATS[0] : work.dials.heat > 0.66 ? HEATS[2] : HEATS[1]).label;

export const NUDGES = [-5, -2, 0, 2, 5] as const;

export function nudgeWord(nudge: number): string {
  if (Math.abs(nudge) < 0.05) return 'turnout as we estimate it';
  return `turnout ${trim(Math.abs(nudge))}% ${nudge > 0 ? 'higher' : 'lower'}`;
}

export const CUSHIONS = [
  { cushion: 0, help: 'The bare minimum to win.' },
  { cushion: 2, help: '' },
  { cushion: 3, help: 'What most trainers plan for. Our number.' },
  { cushion: 5, help: 'Stays clear of a recount.' },
] as const;

/** A share of a two-way race says nothing in a race with several winners, so those get the points alone. */
export function cushionLabel(cushion: number, twoWay: boolean): string {
  if (cushion < 0.05) return twoWay ? '50% + 1 vote' : 'Just enough, no extra';
  return twoWay ? `${50 + cushion}% of a two-way race` : `${trim(cushion)} points extra`;
}

export const cushionWord = (cushion: number) => (cushion < 0.05 ? 'no extra for safety' : `a little extra for safety (${trim(cushion)} points)`);

/** One winner, so the cushion can be put as a share of a two-way race. */
export const onePlace = (work: ReadyWork) => placesOf(work.contest.rule) === 1;

/** The aim with some dials changed and the rest as they are. */
export const aimWith = (work: ReadyWork, patch: Partial<ReadyWork['dials']>) =>
  voteGoal(work.contest, settleDials(work.contest, { ...work.dials, ...patch })).aim;

/** Where a typed goal sits, in words. */
export function checkWords(work: ReadyWork): string | null {
  if (!work.check) return null;
  const { sits, quietAim, busyAim } = work.check;
  const one = !!work.contest.manual;
  const computed = fmt(work.goal.aim);
  switch (sits) {
    case 'Low':
      return one ? `That’s below what your numbers need (${computed}).` : `That’s below what even a quiet year needs (${fmt(quietAim)}).`;
    case 'Within':
      return one ? 'That matches what your numbers need.' : `That’s within our range: ${fmt(quietAim)} in a quiet year to ${fmt(busyAim)} in a busy one.`;
    case 'High':
      return `${one ? `That’s above what your numbers need (${computed})` : `That’s above what a busy year needs (${fmt(busyAim)})`}. More is safer, but every extra vote is more doors.`;
    default:
      return assertNever(sits);
  }
}

const trim = (n: number) => n.toFixed(1).replace(/\.0$/, '');
